import { AuthChallenge, type AuthResponse } from '@nauth-toolkit/client';
import type { Role, User, Workspace } from '@rundown/types';
import { api } from '../../lib/api';
import { authClient } from '../../lib/auth';

/**
 * Every nauth interaction goes through the SDK client in lib/auth.ts. The one
 * exception is the profile: `GET /me` is Rundown's endpoint, not nauth's — nauth
 * has no workspaces or roles, so its own `/auth/profile` cannot answer "which
 * workspaces is this person in, and as what".
 *
 * A signed-in user with no memberships is a real state, not an error: Noorix
 * creates accounts, and one can exist before it has been added to a workspace.
 */
export interface ProfileMembership {
  readonly workspace: Workspace;
  readonly role: Role;
}

export interface AuthProfile {
  readonly user: User;
  /** Empty for a signed-in user who has not been added to a workspace yet. */
  readonly memberships: readonly ProfileMembership[];
}

export function fetchProfile(signal?: AbortSignal): Promise<AuthProfile> {
  return api.get<AuthProfile>('/me', signal);
}

export interface PasswordCredentials {
  readonly email: string;
  readonly password: string;
}

/**
 * A 2xx here does not mean the person is signed in: nauth answers with EITHER
 * tokens or a challenge, and a challenge carries no session. Branch on
 * `signInOutcome`, never on success alone.
 */
export function signInWithPassword(credentials: PasswordCredentials): Promise<AuthResponse> {
  return authClient.login(credentials.email, credentials.password);
}

export type SignInOutcome =
  | { readonly kind: 'signed-in' }
  | { readonly kind: 'must-change-password'; readonly session: string }
  | { readonly kind: 'unsupported-challenge'; readonly challengeName: string };

export function signInOutcome(response: AuthResponse): SignInOutcome {
  if (response.challengeName === undefined) return { kind: 'signed-in' };
  if (response.challengeName === AuthChallenge.FORCE_CHANGE_PASSWORD && response.session !== undefined) {
    return { kind: 'must-change-password', session: response.session };
  }
  return { kind: 'unsupported-challenge', challengeName: response.challengeName };
}

export interface PasswordChallengeInput {
  readonly session: string;
  readonly newPassword: string;
}

/** Answers FORCE_CHANGE_PASSWORD, which both sets the password and completes the sign-in. */
export function respondToPasswordChallenge(input: PasswordChallengeInput): Promise<AuthResponse> {
  return authClient.respondToChallenge({
    type: AuthChallenge.FORCE_CHANGE_PASSWORD,
    session: input.session,
    newPassword: input.newPassword,
  });
}

/** The SDK clears local tokens even when the server call fails — e.g. a session already revoked. */
export function signOut(): Promise<void> {
  return authClient.logout();
}

/**
 * Full-page navigation to nauth's redirect starter. `returnTo` is relative on
 * purpose: the API runs with `allowAbsoluteReturnTo: false`. `appState` is
 * round-tripped untouched so the callback can finish the journey.
 */
export function startGoogleSignIn(next: string): Promise<void> {
  return authClient.loginWithSocial('google', { returnTo: '/auth/callback', appState: next });
}

/** Trades the single-use `exchangeToken` from the OAuth redirect for tokens (or a challenge). */
export function completeSocialSignIn(exchangeToken: string): Promise<AuthResponse> {
  return authClient.exchangeSocialRedirect(exchangeToken);
}

/**
 * Mirrors `password` in apps/api/src/config/auth.config.ts. The API is the only
 * authority — a drift here costs an extra round trip, never a bypass.
 */
export const PASSWORD_POLICY = {
  minLength: 8,
  requireUppercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
} as const;

/** Human-readable policy failures, for inline validation before the request is sent. */
export function passwordPolicyIssues(password: string): string[] {
  const issues: string[] = [];
  if (password.length < PASSWORD_POLICY.minLength) {
    issues.push(`at least ${PASSWORD_POLICY.minLength} characters`);
  }
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(password)) {
    issues.push('an uppercase letter');
  }
  if (PASSWORD_POLICY.requireNumbers && !/[0-9]/.test(password)) {
    issues.push('a number');
  }
  if (PASSWORD_POLICY.requireSpecialChars && !/[^A-Za-z0-9]/.test(password)) {
    issues.push('a symbol');
  }
  return issues;
}

/**
 * Resolves to nothing on purpose. nauth's response carries `destination` only
 * when the identifier matched a real account, so exposing it would hand the
 * screen an account-enumeration oracle the server was built to avoid.
 */
export async function requestPasswordReset(identifier: string): Promise<void> {
  await authClient.forgotPassword(identifier);
}

export interface ResetPasswordInput {
  readonly identifier: string;
  readonly code: string;
  readonly newPassword: string;
}

/** Sets the password but does not sign in — the user proves the new credential at /signin. */
export async function confirmPasswordReset(input: ResetPasswordInput): Promise<void> {
  await authClient.confirmForgotPassword(input.identifier, input.code, input.newPassword);
}
